import React, { useCallback, useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { useParams } from "react-router-dom";
import PublicJobCard from "../../Common/Card/Public-Job-Card/PublicJobCard";
import PublicJobsDetailsContainer from "../../Common/Jobs-Details-Container/Public-Jobs-Details-Container/PublicJobsDetailsContainer";
import AnimationPlayer from "../../Common/Animation-Player/AnimationPlayer";
import Button from "../../Common/Button/Button";
import ContentDialog from "../../Common/Content-Dialog/ContentDialog";
import DynamicSkeleton from "../../Common/Skeleton/Skeleton";
import JobSimpleSearchBox from "../../Common/Simple-Search-Box/JobSimpleSearchBox";
import VybogTALLoaderAnimation from "../../Common/Dynamic-Animations/DynamicAnimations";
import NojobsAnimation from "../../Assets/Json/nodata-found-robot-animation.json";
import dragAndDropIcon from "../../Assets/Images/upload.svg";
import uploadIcon from "../../Assets/Images/upload-resume-icon.png";
import DynamicCandidateCustomField from "../../Common/DynamicCandidateCustomField/DynamicCandidateCustomField";
import { get, post } from "../../helper/Api_helper";
import { VYLABS_BASE_URL, GET_TENANTS_LOGO } from "../../helper/url_helper";
import "./PublicBoardJobs.css";
import { Toast } from "../../Common/Toast-Snackbar/ToastSnackbar";
import {
  applyEllipsis,
  filterPayload,
} from "../../Common/Common-Function/CommonFunctions";
import { TENANT_OTP_API_KEY } from "../../Tenant-Config/TenantConfig";

const allowedResumeTypes = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

const JOBS_LIMIT = 20;
const EMPTY_SEARCH_FILTERS = {
  query: "",
  job_role: "",
  job_type: "",
  location: "",
  skills: "",
  min_years_experience: "",
  max_years_experience: "",
  priority: "",
};

const fetchBoardJobs = (slug, filters, page = 1, offset = 0) => {
  const config = {
    _skipAuth: true,
    headers: { "X-API-Key": TENANT_OTP_API_KEY },
  };

  if (filters) {
    return post(
      VYLABS_BASE_URL,
      `/board/${slug}/search`,
      {
        ...filters,
        page,
        offset,
        limit: JOBS_LIMIT,
      },
      config,
    );
  }

  return get(
    VYLABS_BASE_URL,
    `/board/${slug}/jobs?offset=${offset}&limit=${JOBS_LIMIT}`,
    config,
  );
};

function PublicBoardJobs() {
  const { slug } = useParams();
  const [boardDetails, setBoardDetails] = useState(null);
  const [tenantLogo, setTenantLogo] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [searchFilters, setSearchFilters] = useState(EMPTY_SEARCH_FILTERS);
  const [submittedFilters, setSubmittedFilters] = useState(null);
  const [nextSearchPage, setNextSearchPage] = useState(2);
  const [hasMoreJobs, setHasMoreJobs] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [selectedApplyJob, setSelectedApplyJob] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [openFileUpload, setOpenFileUpload] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [customFieldValues, setCustomFieldValues] = useState({});
  const [customFieldErrors, setCustomFieldErrors] = useState({});
  const fileInputRef = useRef(null);
  const jobsObserverRef = useRef(null);
  const jobsRequestRef = useRef(0);

  const rawCustomFieldDefinitions =
    selectedApplyJob?.custom_field_definitions ||
    selectedApplyJob?.job?.custom_field_definitions ||
    [];
  const customFieldDefinitions = Array.isArray(rawCustomFieldDefinitions)
    ? rawCustomFieldDefinitions
    : [];
  const sortedCustomFieldDefinitions = [...customFieldDefinitions].sort(
    (a, b) => {
      if (a.field_type === "boolean" && b.field_type !== "boolean") return 1;
      if (a.field_type !== "boolean" && b.field_type === "boolean") return -1;
      return 0;
    },
  );
  const hasCustomFields = sortedCustomFieldDefinitions.length > 0;

  useEffect(() => {
    const requestId = ++jobsRequestRef.current;
    const fetchPublicJobs = async () => {
      if (!slug) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setIsLoadingMore(false);
      setJobs([]);
      setHasMoreJobs(false);
      setNextSearchPage(2);

      try {
        const response = await fetchBoardJobs(slug, submittedFilters);

        if (requestId !== jobsRequestRef.current) return;
        const firstJobs = Array.isArray(response?.jobs) ? response.jobs : [];
        const total = response?.total ?? response?.total_jobs;
        setBoardDetails((previous) => ({
          ...previous,
          ...response,
          total: total ?? firstJobs.length,
        }));
        setJobs(firstJobs);
        setHasMoreJobs(
          firstJobs.length > 0 &&
            (response?.has_more ??
              (total != null
                ? firstJobs.length < total
                : firstJobs.length === JOBS_LIMIT)),
        );
      } catch (error) {
        if (requestId !== jobsRequestRef.current) return;
        console.error("Failed to fetch public board jobs:", error);
        setBoardDetails(null);
        setJobs([]);
      } finally {
        if (requestId === jobsRequestRef.current) setIsLoading(false);
      }
    };

    fetchPublicJobs();
    return () => {
      jobsRequestRef.current = requestId + 1;
      jobsObserverRef.current?.disconnect();
    };
  }, [slug, submittedFilters]);

  useEffect(() => {
    let isActive = true;
    setTenantLogo(null);

    const getLogoDetails = async () => {
      if (!slug) return;

      try {
        const response = await get(VYLABS_BASE_URL, GET_TENANTS_LOGO, {
          _skipAuth: true,
          params: { slug },
          headers: {
            "X-API-Key": TENANT_OTP_API_KEY,
          },
        });

        if (isActive) {
          setTenantLogo({ slug, ...response });
        }
      } catch (error) {
        if (isActive) {
          console.error("Failed to fetch tenant logo:", error);
        }
      }
    };

    getLogoDetails();

    return () => {
      isActive = false;
    };
  }, [slug]);

  const logoDetails = tenantLogo?.slug === slug ? tenantLogo : null;
  const tenantName =
    boardDetails?.tenant_name || logoDetails?.tenant_name || "VybogTAL";
  const tenantLogoUrl = logoDetails?.logo_url || null;
  const jobCount = boardDetails?.total ?? jobs.length;
  const canLoadMore = hasMoreJobs;

  const handleSearchSubmit = (params) => {
    if (isLoading) return;
    const filters = {};
    Object.keys(EMPTY_SEARCH_FILTERS).forEach((key) => {
      const value = params[key];
      if (value !== "" && value !== null && value !== undefined) {
        filters[key] = value;
      }
    });
    setSubmittedFilters(filters);
  };

  const handleClearSearch = () => {
    setSearchFilters(EMPTY_SEARCH_FILTERS);
    setSubmittedFilters(null);
  };

  const handleSearchFilterChange = (key) => (event) => {
    const value = event.target.value;
    setSearchFilters((previous) => ({ ...previous, [key]: value }));
  };

  const loadMoreJobs = useCallback(async () => {
    if (!slug || isLoading || isLoadingMore || !canLoadMore) return;
    const requestId = jobsRequestRef.current;

    try {
      setIsLoadingMore(true);
      const response = await fetchBoardJobs(
        slug,
        submittedFilters,
        nextSearchPage,
        jobs.length,
      );
      if (requestId !== jobsRequestRef.current) return;
      const nextJobs = Array.isArray(response?.jobs) ? response.jobs : [];
      const existingJobIds = new Set(jobs.map((job) => job.id));
      const newJobs = nextJobs.filter((job) => !existingJobIds.has(job.id));
      const loadedCount = jobs.length + newJobs.length;
      const total = response?.total ?? response?.total_jobs;
      setBoardDetails((previous) => ({
        ...previous,
        total: total ?? loadedCount,
      }));
      setHasMoreJobs(
        newJobs.length > 0 &&
          (response?.has_more ??
            (total != null
              ? loadedCount < total
              : nextJobs.length === JOBS_LIMIT)),
      );
      setNextSearchPage((page) => page + 1);

      setJobs((previousJobs) => {
        const existingJobIds = new Set(previousJobs.map((job) => job.id));
        return [
          ...previousJobs,
          ...nextJobs.filter((job) => !existingJobIds.has(job.id)),
        ];
      });
    } catch (error) {
      if (requestId !== jobsRequestRef.current) return;
      console.error("Failed to load more public board jobs:", error);
      Toast("error", "Failed to load more jobs.");
    } finally {
      if (requestId === jobsRequestRef.current) setIsLoadingMore(false);
    }
  }, [
    canLoadMore,
    isLoading,
    isLoadingMore,
    jobs,
    slug,
    submittedFilters,
    nextSearchPage,
  ]);

  const lastJobRef = useCallback(
    (node) => {
      if (jobsObserverRef.current) jobsObserverRef.current.disconnect();
      if (!node || !canLoadMore || isLoadingMore) return;

      jobsObserverRef.current = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            jobsObserverRef.current?.disconnect();
            loadMoreJobs();
          }
        },
        { root: null, rootMargin: "100px", threshold: 0.1 },
      );
      jobsObserverRef.current.observe(node);
    },
    [canLoadMore, isLoadingMore, loadMoreJobs],
  );

  useEffect(
    () => () => {
      if (jobsObserverRef.current) jobsObserverRef.current.disconnect();
    },
    [],
  );

  const openPublicJobDetails = (job) => {
    if (!job) return;
    setSelectedJob({
      ...job,
      subTitle: tenantName,
    });
    setIsDetailsOpen(true);
  };
  const resetUploadState = () => {
    setOpenFileUpload(false);
    setSelectedApplyJob(null);
    setSelectedFile(null);
    setDragActive(false);
    setCustomFieldValues({});
    setCustomFieldErrors({});
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const openPublicJobApply = (job) => {
    if (!job) return;
    setSelectedApplyJob(job);
    setCustomFieldValues({});
    setCustomFieldErrors({});
    setOpenFileUpload(true);
  };

  const closePublicJobDetails = () => {
    setIsDetailsOpen(false);
    setSelectedJob(null);
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file && allowedResumeTypes.includes(file.type)) {
      setSelectedFile(file);
      return;
    }

    Toast("error", "Please select a valid PDF, DOC, or DOCX file.");
    setSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleDrag = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();

    if (event.type === "dragenter" || event.type === "dragover") {
      setDragActive(true);
    } else if (event.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);

    const files = Array.from(event.dataTransfer.files || []);
    const validFile = files.find((file) =>
      allowedResumeTypes.includes(file.type),
    );

    if (validFile) {
      setSelectedFile(validFile);
      if (files.length > 1 || files[0] !== validFile) {
        Toast(
          "error",
          "Some files were not valid. Only PDF, DOC, or DOCX files are accepted.",
        );
      }
      return;
    }

    Toast(
      "error",
      "No valid files found. Please upload a PDF, DOC, or DOCX file.",
    );
  }, []);

  const handleDeleteFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleCustomFieldChange = (fieldKey, value) => {
    setCustomFieldValues((previousValues) => ({
      ...previousValues,
      [fieldKey]: value,
    }));
    setCustomFieldErrors((previousErrors) => {
      if (!previousErrors[fieldKey]) return previousErrors;
      const nextErrors = { ...previousErrors };
      delete nextErrors[fieldKey];
      return nextErrors;
    });
  };

  const validateCustomFields = () => {
    const errors = {};

    sortedCustomFieldDefinitions.forEach((field) => {
      const value = customFieldValues[field.field_key];
      const isMissing =
        value === "" ||
        value === null ||
        value === undefined ||
        (Array.isArray(value) && value.length === 0);

      if (field.required && isMissing) {
        errors[field.field_key] = `${field.label || "This field"} is required`;
      }
    });

    setCustomFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      Toast("error", "Please upload a resume before submitting.");
      return;
    }

    if (!slug || !selectedApplyJob?.id) {
      Toast("error", "Unable to apply for this job.");
      return;
    }

    if (!validateCustomFields()) {
      Toast("error", "Please fill all required custom fields.");
      return;
    }

    try {
      setIsUploading(true);
      const formData = new FormData();
      formData.append("file", selectedFile);
      const customFieldsPayload = filterPayload(customFieldValues);
      if (Object.keys(customFieldsPayload).length > 0) {
        formData.append("custom_fields", JSON.stringify(customFieldsPayload));
      }

      await post(
        VYLABS_BASE_URL,
        `/board/${slug}/jobs/${selectedApplyJob.id}/apply`,
        formData,
        {
          _skipAuth: true,
          headers: {
            "X-API-Key": TENANT_OTP_API_KEY,
          },
        },
      );
      Toast("success", "Resume uploaded successfully!");
      resetUploadState();
    } catch (error) {
      console.error("Error uploading file:", error);
      Toast("error", "Failed to upload resume.");
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>{tenantName} - Public Jobs</title>
        <meta
          name="description"
          content={`Explore ${tenantName} public job openings.`}
        />
      </Helmet>

      <div className="public-board-page">
        <main className="public-board-content">
          <div
            style={{
              position: "sticky",
              top: 0,
              zIndex: 10,
              backgroundColor: "var(--body__background)",
              display: "flow-root",
            }}
          >
            {(!isLoading || Boolean(boardDetails)) && (
              <section className="public-board-heading">
                {tenantLogoUrl && (
                  <>
                    <img
                      className="public-board-tenant-logo"
                      src={tenantLogoUrl}
                      alt={`${tenantName} logo`}
                      onError={() =>
                        setTenantLogo((current) =>
                          current?.slug === slug
                            ? { ...current, logo_url: null }
                            : current,
                        )
                      }
                    />
                    <span
                      className="public-board-heading-divider"
                      aria-hidden="true"
                    />
                  </>
                )}
                <div className="public-board-heading-text">
                  <h1 className="public-board-title">{tenantName}</h1>
                  {jobCount > 0 && (
                    <span className="public-board-job-count">
                      {jobCount} job{jobCount === 1 ? "" : "s"} available
                    </span>
                  )}
                </div>
              </section>
            )}
            {(Boolean(boardDetails) || Boolean(submittedFilters)) && (
              <div className="public-board-search">
                <JobSimpleSearchBox
                  queryValue={searchFilters.query}
                  onQueryChange={handleSearchFilterChange("query")}
                  jobRoleValue={searchFilters.job_role}
                  onJobRoleChange={handleSearchFilterChange("job_role")}
                  jobTypeValue={searchFilters.job_type}
                  onJobTypeChange={handleSearchFilterChange("job_type")}
                  locationValue={searchFilters.location}
                  onLocationChange={handleSearchFilterChange("location")}
                  skillsValue={searchFilters.skills}
                  onSkillsChange={handleSearchFilterChange("skills")}
                  minYearsExperienceValue={searchFilters.min_years_experience}
                  onMinYearsExperienceChange={handleSearchFilterChange(
                    "min_years_experience",
                  )}
                  maxYearsExperienceValue={searchFilters.max_years_experience}
                  onMaxYearsExperienceChange={handleSearchFilterChange(
                    "max_years_experience",
                  )}
                  priorityValue={searchFilters.priority}
                  onPriorityChange={handleSearchFilterChange("priority")}
                  onSubmit={handleSearchSubmit}
                  onClearForm={handleClearSearch}
                  hasSearched={Boolean(submittedFilters)}
                  isSimpleSearchLoading={isLoading}
                  recommendedSearchBoxTitle="Job Search"
                  subText="Find the perfect job opportunity"
                  advancedFiltersInSidePopup
                  publicSearch
                />
              </div>
            )}
          </div>

          {isLoading ? (
            <div className="public-board-state">
              <VybogTALLoaderAnimation />
            </div>
          ) : jobs.length === 0 ? (
            <div className="public-board-empty">
              <p>
                {submittedFilters
                  ? "No jobs match your search."
                  : "No active jobs found."}
              </p>
              <AnimationPlayer
                animationSrc={NojobsAnimation}
                playerHeight="240px"
                playerWidth="280px"
              />
            </div>
          ) : (
            <div className="public-board-grid">
              {jobs.map((job) => (
                <PublicJobCard
                  key={job.id}
                  title={job.title}
                  jobRole={job.job_role || job.title}
                  client={tenantName}
                  client_contact_person=""
                  location={job.location}
                  jobtype={job.job_type}
                  minYearsExperience={job.years_experience}
                  maxYearsExperience={job.max_years_experience}
                  experience={job.years_experience}
                  actionText
                  viewJobEventHandler={() => openPublicJobDetails(job)}
                  applyJobEventHandler={() => openPublicJobApply(job)}
                  showTitleTooltip={false}
                />
              ))}
              {isLoadingMore && (
                <>
                  {Array.from({ length: 6 }).map((_, idx) => (
                    <DynamicSkeleton
                      key={`public-board-scroll-skeleton-${idx}`}
                      variant="rectangular"
                      dimensions={{
                        width: "100%",
                        height: "185px",
                        sx: {
                          borderRadius: "16px",
                          boxShadow: "0 2px 4px rgb(159 159 159 / 10%)",
                        },
                      }}
                    />
                  ))}
                </>
              )}
              {canLoadMore && !isLoadingMore && (
                <div
                  ref={lastJobRef}
                  aria-hidden="true"
                  style={{ gridColumn: "1 / -1", height: "1px" }}
                />
              )}
            </div>
          )}
        </main>
        <PublicJobsDetailsContainer
          selectedJob={selectedJob}
          isOpen={isDetailsOpen}
          onClose={closePublicJobDetails}
        />
        <ContentDialog
          isOpen={openFileUpload}
          onClose={resetUploadState}
          onCloseIconClose={resetUploadState}
          title={`Apply for ( ${applyEllipsis(selectedApplyJob?.title, 80)} )`}
          contentWidth="40vw"
          overflowY={hasCustomFields}
          submitButtonLabel={"Upload"}
          cancelButtonLabel="Cancel"
          event={handleUpload}
          disabled={isUploading || !selectedFile}
        >
          <div className="public-apply-upload-container">
            <div
              className={`public-apply-file-wrapper${
                dragActive ? " drag-active" : ""
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <input
                type="file"
                id="public-resume-upload"
                accept="application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                onChange={handleFileChange}
                ref={fileInputRef}
                className="public-apply-file-input"
              />
              <label
                htmlFor="public-resume-upload"
                className="public-apply-file-label"
              >
                <span className="public-apply-drop-info">
                  <img
                    src={dragAndDropIcon}
                    alt="upload"
                    className="public-apply-drag-icon"
                  />
                  <span>
                    {dragActive
                      ? "Drop your resume here"
                      : "Drag & Drop or Click to upload resume"}
                  </span>
                  <small>Supported formats: PDF, DOC, DOCX</small>
                </span>
              </label>
            </div>

            {selectedFile && (
              <div className="public-apply-file-name-container">
                <div className="public-apply-file-name">
                  <img src={uploadIcon} alt="upload" />
                  <span>{selectedFile.name}</span>
                </div>
                <Button
                  label="Remove"
                  event={handleDeleteFile}
                  hoverd={false}
                  backgroundColor="transparent"
                  color="#ff5d37"
                  border="none"
                  style={{ padding: "4px 0" }}
                />
              </div>
            )}

            {hasCustomFields && (
              <div className="public-apply-custom-fields-container">
                {sortedCustomFieldDefinitions.map((field) => (
                  <div
                    key={field.field_key}
                    className="public-apply-custom-field"
                  >
                    <DynamicCandidateCustomField
                      entity_type={field.entity_type}
                      field_key={field.field_key}
                      label={field.label}
                      field_type={field.field_type}
                      required={field.required}
                      options={field.options}
                      value={customFieldValues[field.field_key] || ""}
                      onChange={(value) =>
                        handleCustomFieldChange(field.field_key, value)
                      }
                      canCreateTextFieldLabelStyle={{ marginBottom: "10px" }}
                      textfieldType={
                        field.field_type === "text"
                          ? "text"
                          : field.field_type === "integer" ||
                              field.field_type === "decimal"
                            ? "number"
                            : undefined
                      }
                    />
                    {customFieldErrors[field.field_key] && (
                      <div className="public-apply-custom-field-error">
                        {customFieldErrors[field.field_key]}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </ContentDialog>
      </div>
    </>
  );
}

export default PublicBoardJobs;
